import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Loader2, Shield, CheckCircle, Phone, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

// Country codes for dropdown
const COUNTRY_CODES = [
  { code: '+91', country: 'IN', name: 'India' },
  { code: '+971', country: 'AE', name: 'UAE' },
  { code: '+1', country: 'US', name: 'USA' },
  { code: '+44', country: 'GB', name: 'UK' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia' },
  { code: '+968', country: 'OM', name: 'Oman' },
  { code: '+974', country: 'QA', name: 'Qatar' },
  { code: '+965', country: 'KW', name: 'Kuwait' },
  { code: '+973', country: 'BH', name: 'Bahrain' },
  { code: '+65', country: 'SG', name: 'Singapore' },
  { code: '+61', country: 'AU', name: 'Australia' },
  { code: '+49', country: 'DE', name: 'Germany' },
  { code: '+33', country: 'FR', name: 'France' },
  { code: '+81', country: 'JP', name: 'Japan' },
  { code: '+86', country: 'CN', name: 'China' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OTPInput = ({ length = 6, value, onChange, disabled }) => {
  const inputRefs = useRef([]);

  const handleChange = (index, e) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return;

    const newOtp = value.split('');
    newOtp[index] = val.slice(-1);
    const newValue = newOtp.join('');
    onChange(newValue);

    // Auto-focus next input
    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pastedData);
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length }).map((_, index) => (
        <input
          key={`otp-digit-${index}`}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-200 rounded-xl focus:border-[#0047AB] focus:ring-2 focus:ring-[#0047AB]/20 outline-none transition-all disabled:bg-slate-100"
          data-testid={`otp-input-${index}`}
        />
      ))}
    </div>
  );
};

const OTPLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithOTP, isAuthenticated } = useAuth();
  
  const [step, setStep] = useState('identifier'); // identifier, otp, success
  const [purpose, setPurpose] = useState('login');
  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('+91'); // Default to India
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  const from = location.state?.from?.pathname || '/';
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from);
    }
  }, [isAuthenticated, navigate, from]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSendOTP = async (e) => {
    e?.preventDefault();
    
    if (!identifier.trim()) {
      toast.error('Please enter your email');
      return;
    }

    if (purpose === 'signup' && !name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    // Combine country code and phone number
    const fullPhoneNumber = phoneNumber.trim() ? `${countryCode}${phoneNumber.trim()}` : undefined;

    setLoading(true);
    try {
      await axios.post(`${API}/auth/otp/send`, {
        identifier: identifier.trim(),
        identifier_type: 'email',
        purpose,
        name: name.trim() || undefined,
        phone_number: purpose === 'signup' ? fullPhoneNumber : undefined
      });
      
      setStep('otp');
      setResendTimer(60);
      toast.success('OTP sent to your email');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send OTP';
      toast.error(message);
      
      // If account exists/not found, suggest switching
      if (message.includes('already exists')) {
        setPurpose('login');
      } else if (message.includes('not found')) {
        setPurpose('signup');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e?.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }

    // Combine country code and phone number
    const fullPhoneNumber = phoneNumber.trim() ? `${countryCode}${phoneNumber.trim()}` : undefined;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/otp/verify`, {
        identifier: identifier.trim(),
        identifier_type: 'email',
        otp,
        purpose,
        name: name.trim() || undefined,
        phone_number: purpose === 'signup' ? fullPhoneNumber : undefined
      });
      
      // Update auth context
      await loginWithOTP(response.data);
      
      setStep('success');
      toast.success(purpose === 'signup' ? 'Account created successfully!' : 'Welcome back!');
      
      setTimeout(() => navigate(from), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/otp/resend`, {
        identifier: identifier.trim(),
        identifier_type: 'email',
        purpose,
        name: name.trim() || undefined
      });
      
      setResendTimer(60);
      toast.success('New OTP sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50 flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0047AB] to-[#0066FF] p-6 text-white text-center">
            <Link to="/" className="inline-block mb-4">
              <h1 className="text-2xl font-outfit font-bold tracking-tighter">
                DXB<span className="text-blue-200">2</span>BOM
              </h1>
            </Link>
            <p className="text-blue-100 text-sm">
              {purpose === 'signup' ? 'Create your account' : 'Welcome back'}
            </p>
          </div>

          <div className="p-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Enter Identifier */}
              {step === 'identifier' && (
                <motion.div
                  key="identifier"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  {/* Toggle Login/Signup */}
                  <div className="flex bg-slate-100 rounded-lg p-1 mb-6">
                    <button
                      onClick={() => setPurpose('login')}
                      className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                        purpose === 'login' 
                          ? 'bg-white text-[#0047AB] shadow-sm' 
                          : 'text-slate-600'
                      }`}
                      data-testid="toggle-login"
                    >
                      Login
                    </button>
                    <button
                      onClick={() => setPurpose('signup')}
                      className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                        purpose === 'signup' 
                          ? 'bg-white text-[#0047AB] shadow-sm' 
                          : 'text-slate-600'
                      }`}
                      data-testid="toggle-signup"
                    >
                      Sign Up
                    </button>
                  </div>

                  <form onSubmit={handleSendOTP} className="space-y-4">
                    {purpose === 'signup' && (
                      <div>
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Enter your name"
                          className="mt-1.5"
                          data-testid="otp-name-input"
                        />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="identifier">Email Address</Label>
                      <Input
                        id="identifier"
                        type="email"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        placeholder="name@example.com"
                        className="mt-1.5"
                        data-testid="otp-identifier-input"
                      />
                    </div>

                    {purpose === 'signup' && (
                      <div>
                        <Label htmlFor="phone">Phone Number <span className="text-slate-400 font-normal">(Optional)</span></Label>
                        <div className="flex gap-2 mt-1.5">
                          {/* Country Code Dropdown */}
                          <div className="relative" ref={dropdownRef}>
                            <button
                              type="button"
                              onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                              className="flex items-center justify-between gap-1 h-10 px-3 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors min-w-[90px]"
                              data-testid="country-code-dropdown"
                            >
                              <span className="text-sm font-medium">{countryCode}</span>
                              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {showCountryDropdown && (
                              <div className="absolute z-50 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                                {COUNTRY_CODES.map((cc) => (
                                  <button
                                    key={cc.code}
                                    type="button"
                                    onClick={() => {
                                      setCountryCode(cc.code);
                                      setShowCountryDropdown(false);
                                    }}
                                    className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                                      countryCode === cc.code ? 'bg-blue-50 text-[#0047AB]' : ''
                                    }`}
                                    data-testid={`country-option-${cc.country}`}
                                  >
                                    <span>{cc.name}</span>
                                    <span className="text-slate-500">{cc.code}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Phone Number Input */}
                          <Input
                            id="phone"
                            type="tel"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d]/g, ''))}
                            placeholder="9876543210"
                            className="flex-1"
                            data-testid="otp-phone-input"
                          />
                        </div>
                        <p className="text-xs text-slate-400 mt-1">For profile purposes only. OTP will be sent to email.</p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-[#0047AB] hover:bg-[#003380]"
                      data-testid="send-otp-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Shield className="w-4 h-4 mr-2" />
                          Send OTP
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 text-center">
                    <Link
                      to="/"
                      className="text-sm text-slate-600 hover:text-[#0047AB]"
                    >
                      ← Back to Home
                    </Link>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Enter OTP */}
              {step === 'otp' && (
                <motion.div
                  key="otp"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <button
                    onClick={() => { setStep('identifier'); setOtp(''); }}
                    className="flex items-center gap-1 text-slate-600 hover:text-slate-900 mb-4"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm">Back</span>
                  </button>

                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-[#0047AB]" />
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-2">Check Your Email</h2>
                    <p className="text-sm text-slate-600">
                      We've sent a 6-digit code to<br />
                      <span className="font-medium text-slate-900">{identifier}</span>
                    </p>
                  </div>

                  <form onSubmit={handleVerifyOTP} className="space-y-6">
                    <OTPInput
                      length={6}
                      value={otp}
                      onChange={setOtp}
                      disabled={loading}
                    />

                    <Button
                      type="submit"
                      disabled={loading || otp.length !== 6}
                      className="w-full h-12 bg-[#0047AB] hover:bg-[#003380]"
                      data-testid="verify-otp-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        'Verify & Continue'
                      )}
                    </Button>
                  </form>

                  <div className="mt-4 text-center">
                    <p className="text-sm text-slate-600">
                      Didn't receive the code?{' '}
                      {resendTimer > 0 ? (
                        <span className="text-slate-400">Resend in {resendTimer}s</span>
                      ) : (
                        <button
                          onClick={handleResendOTP}
                          disabled={loading}
                          className="text-[#0047AB] font-medium hover:underline"
                        >
                          Resend OTP
                        </button>
                      )}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Success */}
              {step === 'success' && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">
                    {purpose === 'signup' ? 'Account Created!' : 'Welcome Back!'}
                  </h2>
                  <p className="text-sm text-slate-600">
                    Redirecting you...
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Google Login Option */}
            {step === 'identifier' && (
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-slate-500">or</span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full mt-4 h-12 border-slate-200"
                  onClick={() => {
                    const redirectUrl = window.location.origin + '/auth/callback';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  data-testid="google-login-btn"
                >
                  <img
                    src="https://www.google.com/favicon.ico"
                    alt="Google"
                    className="w-5 h-5 mr-2"
                  />
                  Continue with Google
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-500 mt-6">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-[#0047AB] hover:underline">Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-[#0047AB] hover:underline">Privacy Policy</Link>
        </p>
      </motion.div>
    </div>
  );
};

export default OTPLoginPage;
